<template>
  <div class="film-wrap" :class="mode === 'only' && `device-${device}`">
    <div class="film-scroll">
      <div v-if="isLoading"
           class="loading_indicator">
        <div class="loading circle">
          <svg x="0px" y="0px" viewBox="0 0 150 150">
            <circle class="loading-inner" cx="75" cy="75" r="60"/>
          </svg>
          <p class="loading_notice">{{ $t('common.loadingDot') }}</p>
        </div>
      </div>
      <div class="scroll-wrap"
           :class="{isHover: index === filmIndex, notHover: filmIndex !== null && index !== filmIndex}"
           v-for="(item, index) in dailyScrollData"
           :key="index"
           :style="scrollWrapStyle(index)"
           :data-title="filmDate(item.date)"
      >
        <div class="heatmap_scroll"
             :style="heatmapScrollStyle(index)"
             @mouseenter="onHeatmapScroll(index)"
             @mouseleave="leaveHeatmapScroll(index)"
             ref="heatmapScroll">
          <div class="heatmap-bg"></div>
          <svg v-if="svgPath && isAttention" class="attention_scroll" :width="hoverWidth" :height="filmHeight">
            <path :d="svgPath(index)"
                  stroke-width="2"
                  stroke="#fff"
                  fill="rgba(20, 20, 20, 0.7)"></path>
          </svg>
          <canvas class="scroll__canvas" ref="canvas"
                  @mousemove="onCanvasMouseMove"></canvas>
          <div
            class="movable__marker"
            v-if="currentAttentionData && isAttention && index === filmIndex"
            :style="tooltipStyle">
            <div
              :class="{ left: index >= Math.round(dailyScrollData.length / 2),
                          top: currentAttentionData.top <= 50,
                          bottom: currentAttentionData.top >= 1655,
                          ja: $i18n.locale === 'ja'}"
            >
              <i class="arrow">
              </i>
              <span v-html="$t('heatmap.filmstripScrollAttention',{ attention: currentAttentionData.attention, percentage: currentAttentionData.percentage })">
                </span>
            </div>
          </div>
          <HeatmapAvgFold
            v-if="index === filmIndex && item.avgFold"
            :avg-fold-data="item"
            :scale="scale"
            :film-index="filmIndex"
            :fit-mode="fitMode"
            :is-attention="isAttention"
          />
          <template v-if="index === filmIndex">
            <div class="fixed__marker_film"
                 v-for="(item, index) in reachItems[index]"
                 :key="index"
                 :style="{ top: item.reach + 'px' }">
              <div class="percentage">{{ item.percentage }}%</div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format, isSameMonth } from 'date-fns'
import { getCaptureImageUrl, getDailyScrollData } from '@/api/report'
import { normalizeScrollData } from '@/libs/report-data-normalizer'
import HeatmapAvgFold from '@/components/heatmap/HeatmapAvgFold.vue'
import { mapState } from 'vuex'

export default {
  name: 'HeatmapFilmStrip',
  components: { HeatmapAvgFold },
  props: {
    avgFoldData: {
      type: Object,
      default: null
    },
    isAttention: {
      type: Boolean,
      default: false
    },
    isFilmToggle: {
      type: Boolean,
      default: false
    },
    captureImageParams: {
      type: Object,
      default: () => {}
    },
    captureImageTimestamp: {
      type: Number,
      default: 0
    },
    ratio: {
      type: Number,
      default: 1
    },
    apiParams: {
      type: Object,
      default: null
    },
    freqPv: {
      type: Array,
      default: () => []
    },
    isFrequency: {
      type: Boolean,
      default: false
    },
    captureWidth: {
      type: Number,
      default: 0
    },
    captureHeight: {
      type: Number,
      default: 0
    },
    mode: {
      type: String,
      default: 'scroll'
    },
    device: {
      type: String,
      default: 'desktop'
    }
  },
  data () {
    return {
      isLoadedOnce: false,
      attentionItems: [],
      currentAttentionData: null,
      fitMode: 'width',
      reachItems: [],
      dailyScrollData: null,
      filmIndex: null,
      svgData: [],
      isLoading: false,
      filmImageUrl: [],
      avgData: 0
    }
  },
  created () {
  },
  mounted () {
    this.loadDailyScrollData()
  },
  destroyed () {
    this.clearHeatmap()
  },
  watch: {
    isFilmToggle (value) {
      if (value) {
        this.loadDailyScrollData()
      }
    }
  },
  computed: {
    ...mapState([
      'applyData'
    ]),
    svgPath () {
      return (index) => {
        if (!this.attentionItems.length) {
          return null
        }
        let pathString = 'M 0 0 L '

        pathString += this.attentionItems[index].map(item => {
          return `${item.dx} ${item.dy}`
        }).join(' L ')

        pathString += ' H 0 Z'
        return pathString
      }
    },
    attentionSet () {
      const data = {}
      for (const item of this.attentionItems[this.filmIndex]) {
        data[item.topPercentage] = item
      }
      return data
    },
    hoverWidth () {
      const calculate = (this.filmWidth * 0.5) * (this.dailyScrollData.length - 1)
      const result = calculate + this.filmWidth
      return result
    },
    filmWidth () {
      return Math.floor(650 / this.dailyScrollData.length)
    },
    scrollWrapStyle () {
      return (index) => {
        return {
          width: index === this.filmIndex ? `${this.hoverWidth}px` : this.filmIndex === null ? `${this.filmWidth}px` : `${this.filmWidth * 0.5}px`,
          height: `${this.filmHeight}px`
        }
      }
    },
    heatmapScrollStyle () {
      return (index) => {
        return {
          height: `${this.filmHeight}px`,
          backgroundImage: `url(${this.filmImageUrl[index]})`
        }
      }
    },
    tooltipStyle () {
      return {
        top: `${this.currentAttentionData.top}px`
      }
    },
    filmDate () {
      return (item) => {
        const date = this.getFilmDate()
        if (date[0][0] === item) {
          return `${format(item, 'MMM')}\n${format(item, 'D')}`
        } else if (date[1][0] === item) {
          return `${format(item, 'MMM')}\n${format(item, 'D')}`
        } else {
          return `\n${format(item, 'D')}`
        }
      }
    },
    scale () {
      if (this.captureWidth && this.hoverWidth) {
        return this.hoverWidth / this.captureWidth
      }
      return 0
    },
    filmHeight () {
      return this.hoverWidth / this.ratio
    }
  },
  methods: {
    async loadDailyScrollData () {
      this.isLoading = true
      const params = {
        ...this.apiParams
      }

      if (this.freqPv && this.isFrequency) {
        params.freq = this.freqPv
      } else if (JSON.parse(localStorage.getItem('paramData'))) {
        params.freq = JSON.parse(localStorage.getItem('paramData')).freqPv
      }

      getDailyScrollData(params, (this.applyData || {}).timezone).then(data => {
        if (!data || Object.entries(data).length <= 1) {
          throw new Error('NO_SCROLL_DATA')
        }
        let normalizedData = data.map(item => {
          if (!item.data.scroll) {
            return null
          }
          return normalizeScrollData(item.data, this.captureHeight, params)
        })

        for (let i = 0; i < data.length; i++) {
          if (normalizedData[i] === null) {
            normalizedData[i] = {
              h20: 0,
              h40: 0,
              h60: 0,
              h80: 0,
              reachItems: [],
              attentionItems: [],
              date: data[i].date,
              avgFold: data[i].data.avg_fold
            }
          } else {
            normalizedData[i].date = data[i].date
            normalizedData[i].avgFold = data[i].data.avg_fold
          }
        }

        this.dailyScrollData = normalizedData
        this.loadImageUrl(data)

        this.isLoading = false
        this.$emit('loadedFilmStrip', this.dailyScrollData, this.hoverWidth)
      })
    },
    async loadImageUrl (data) {
      const promises = data.map(async (item) => {
        const params = {
          ...this.captureImageParams,
          endDate: item.date
        }
        const url = await getCaptureImageUrl(params, this.captureImageTimestamp)
        return url
      })

      this.filmImageUrl = await Promise.all(promises)

      this.$nextTick(() => {
        // this.loadImageUrl(data)
        this.loadHeatmap()
      })
    },
    clearHeatmap () {
      const ctx = this.canvas.getContext('2d')
      ctx.clearRect(0, 0, this.hoverWidth, this.filmHeight)
    },
    loadHeatmap () {
      this.isLoadedOnce = true
      const list = this.dailyScrollData
      const existsAvgFold = []
      for (let i = 0; i < list.length; i++) {
        const item = list[i]
        if (item.h20 || item.h40 || item.h60 || item.h80) {
          // this.renderCanvas(item.h20, item.h40, item.h60,
          //   item.h80, index)
          this.renderCanvas(item.h20, item.h40, item.h60,
            item.h80, i)
        }
        if (item.avgFold) {
          this.avgData += this.scale * item.avgFold
        }
        existsAvgFold.push(item.avgFold)
      }
      // this.dailyScrollData.forEach((item, index) => {
      //   if (item.h20 || item.h40 || item.h60 || item.h80) {
      //     // this.renderCanvas(item.h20, item.h40, item.h60,
      //     //   item.h80, index)
      //     this.renderCanvas(item.h20, item.h40, item.h60,
      //       item.h80, index)
      //   }
      //   if (item.avgFold) {
      //     this.avgData += this.scale * item.avgFold
      //   }
      // })
      // const existsAvgFold = this.dailyScrollData.filter(item => item.avgFold)
      const avgFold = (this.avgData / existsAvgFold.length) + 35
      this.$emit('averageData', avgFold)
      this.setAttentionItems()
      this.setReachItems()
    },
    setReachItems () {
      this.reachItems = this.dailyScrollData.map(item => {
        return item.reachItems.map(item2 => {
          return {
            reach: item2.reach * this.filmHeight,
            percentage: item2.percentage
          }
        })
      })
    },
    setAttentionItems () {
      const aHeight = this.filmHeight / 100

      this.attentionItems = this.dailyScrollData.map((item, i) => {
        const width = this.filmIndex === i ? this.hoverWidth : this.filmIndex === null ? this.filmWidth : this.filmWidth * 0.5
        const padding = this.filmIndex === i ? 20 : this.filmIndex === null ? 5 : 2
        return item.attentionItems.map(item2 => {
          return {
            dx: item2.dx * (width - padding),
            dy: item2.dy * aHeight,
            topPercentage: item2.topPercentage,
            percentage: Math.round(item2.percentage),
            attention: item2.attention
          }
        })
      })
    },
    renderCanvas (h20, h40, h60, h80, index = null) {
      this.canvas = this.$refs.canvas[index]

      const scaleFactor = 10

      if (this.canvas) {
        this.canvas = Array.isArray(this.canvas) ? this.canvas[0] : this.canvas

        if (this.canvas && typeof this.canvas.setAttribute === 'function') {
          this.canvas.setAttribute('width', this.hoverWidth / scaleFactor)
          this.canvas.setAttribute('height', this.filmHeight)
          // const ctx = this.canvas.getContext('2d')
          const offscreenCanvas = new OffscreenCanvas(this.hoverWidth / scaleFactor, this.filmHeight)
          const ctx = offscreenCanvas.getContext('2d')
          ctx.globalAlpha = 1
          const opacity = 0.5
          const gradient = ctx.createLinearGradient(0, 0, 0, this.filmHeight)
          gradient.addColorStop(0, `rgba(255, 0, 0, ${opacity})`)
          gradient.addColorStop(h80, `rgba(255, 255, 0, ${opacity})`)
          gradient.addColorStop(h60, `rgba(0, 255, 0, ${opacity})`)
          gradient.addColorStop(h40, `rgba(0, 255, 255, ${opacity})`)
          gradient.addColorStop(h20, `rgba(0, 0, 255, ${opacity})`)
          gradient.addColorStop(1, `rgba(0, 0, 0, ${opacity})`)
          ctx.fillStyle = gradient
          ctx.fillRect(0, 0, this.hoverWidth / scaleFactor, this.filmHeight)

          const mainCtx = this.canvas.getContext('2d')
          mainCtx.drawImage(offscreenCanvas, 0, 0)
        }
      }
    },
    onCanvasMouseMove (event) {
      const topPosition = event.offsetY
      const topPercentage = Math.ceil(this.getPercentage(topPosition, this.filmHeight))
      const data = this.attentionSet[topPercentage]

      if (!data) {
        return
      }

      this.currentAttentionData = {
        top: topPosition,
        percentage: Math.round(data.percentage),
        attention: data.attention,
        transform: `translateX(96%)`
      }
    },
    getPercentage (value, total) {
      return (value / total) * 100
    },
    onHeatmapScroll (index) {
      this.filmIndex = index
      this.setAttentionItems(index)
    },
    leaveHeatmapScroll () {
      this.currentAttentionData = null
      this.filmIndex = null
      this.setAttentionItems()
    },
    getFilmDate () {
      const sameDate = []
      const differentDate = []
      const list = this.dailyScrollData
      for (let i = 0; i < list.length; i++) {
        const item = list[i]
        if (isSameMonth(item.date, list[0].date)) {
          sameDate.push(item.date)
        } else {
          differentDate.push(item.date)
        }
      }
      // const sameDate = this.dailyScrollData.filter((item, index, array) => {
      //   console.log(item, array)
      //   return isSameMonth(item.date, array[0].date)
      // }).map(item => item.date)
      //
      // const differentDate = this.dailyScrollData.filter((item, index, array) => {
      //   return !isSameMonth(item.date, array[0].date)
      // }).map(item => item.date)

      return [sameDate, differentDate]
    }
  }
}
</script>

<style lang="scss" scoped>

.film-wrap {
  width: 650px;
  height: 100%;

  &.device-phone {
    width: 388px;

    .film-scroll {
      .scroll-wrap {
        &::before {
          font-size: 10px;
        }
      }
    }
  }

  .film-scroll {
    display: flex;
    width: 100%;
    height: 100%;

    .scroll-wrap {
      margin: 0 1px;
      //transition: all .2s ease-in-out;
      flex-grow: 1;
      text-align: center;
      position: relative;
      white-space: pre-wrap;

      &::before {
        content: attr(data-title);
        position: absolute;
        top: 0;
        left: 50%;
        font-weight: 400;
        font-size: 12px;
        color: #D7D7D7;
        transform: translateX(-50%);
        line-height: 1.3
      }

      &.isHover {
        &::before {
          font-size: 15px;
          line-height: 1;
        }
      }
      &.notHover {
        &::before {
          font-size: 9px;
          line-height: 1.8;
        }
      }
    }

    .heatmap_scroll {
      margin-top: 35px;
      width: 100%;
      position: relative;
      box-sizing: border-box;
      background-repeat: no-repeat;
      background-position: center;
      background-size: auto 100%;

      .heatmap-bg {
        opacity: 0.7;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #282b39;
        content: "";
      }

      .scroll__canvas {
        width: 100%;
        height: 100%;
      }

      .fixed__marker_film {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        border-bottom: 1px solid #fff;
        z-index: 200;
        box-shadow: 0 0 3px 0 rgba(45, 45, 45, 0.3);
        pointer-events: none;
        margin-right: 47px;
      }

      .fixed__marker_film .percentage {
        color: #fff;
        top: -10px;
        right: -6px;
        line-height: 18px;
        position: absolute;
        z-index: 200;
        font-size: 11px;
        background-image: url(../../assets/img/heatmap/heatmap_scroll_fixed_maker.svg);
        background-repeat: no-repeat;
        font-weight: 600;
        width: 66px;
        height: 39px;
        background-position: 10px -5px;
        transform: translate(41px);
        box-sizing: border-box;
        padding-right: 6px;
        text-align: right;
      }

      .movable__marker {
        width: 100%;
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        border-bottom: 1px solid #000;
        z-index: 210;
        box-shadow: 0 2px 3px 0 rgba(45, 45, 45, 0.3);

        & > div {
          display: flex;
          align-items: center;
          width: 150px;
          color: #fff;
          position: absolute;
          top: -48px;
          left: 372px;
          z-index: 99;
          padding: 12px 12px;
          border-radius: 8px;
          background: rgba(0,0,0,0.7);
          transform: translate(0%, 0);
          box-sizing: border-box;

          &.ja {
            padding: 12px 1px 12px 12px;
          }

          span {
            font-size: 13px;
            text-align: left;
            word-break: break-word;
            white-space: normal;
            line-height: 120%;
          }

          .arrow {
            content: '';
            position: absolute;
            z-index: 2;
            transform: translateY(-50%) rotate(-90deg);
            border: 7px solid transparent;
            border-bottom-color: rgba(0, 0, 0, .7);
            width: 0;
            height: 0;
            top: 50%;
            right: 100%;
          }

          &.left {
            left: 0;
            right: 100%;
            transform: translate(-105%, 0%);
            .arrow {
              top: 50%;
              left: 100%;
              transform: translateY(-50%) rotate(90deg);
            }
          }

          &.top {
            border-top-left-radius: 0;
            top: 100%;
            right: 100%;
            .arrow {
              top: -7px;
              right: 143px;
              transform: rotate(45deg);
            }

            &.left {
              border-top-left-radius: 8px;
              border-top-right-radius: 0;
              top: 100%;
              left: 0;
              right: 100%;
              transform: translate(-105%, 0);
              .arrow {
                top: -7px;
                left: 143px;
                transform: rotate(-45deg);
              }
            }
          }

          &.bottom {
            border-bottom-left-radius: 0;
            top: 10%;
            left: 103%;
            transform: translateY(-98%);
            .arrow {
              top: 100%;
              right: 143px;
              transform: translateY(-50%) rotate(135deg);
            }

            &.left {
              border-bottom-left-radius: 8px;
              border-bottom-right-radius: 0;
              top: 0;
              left: 0;
              right: 100%;
              transform: translate(-107%, -99%);
              .arrow {
                top: 100%;
                left: 100%;
                transform: translate(-50%, -50%) rotate(-135deg);
              }
            }
          }

        }

        & > span {
          display: inline-block;
          position: absolute;
          top: 0;
          border-bottom: 1px solid blue;
          width: 50px;
          height:10px;

          &.border-left {
            left: 0px;
          }
          &.border-right {
            right: 0px;
          }
        }

      }

      .attention_scroll {
        position: absolute;
        z-index: 0;
        width: 100%;
        top: 0;
        left: 0;

        path {
          transition: all .2s;
        }
      }
    }

  }

}
</style>
